import { zodResolver } from "@hookform/resolvers/zod";
import { LoadingButton } from "@mui/lab";
import {
    Button,
    Card,
    Checkbox,
    Container,
    FormControlLabel,
    Link,
    Stack,
    styled,
    TextField,
    Typography,
} from "@mui/material";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation } from "react-query";
import { useDispatch } from "react-redux";
import z from "zod";
import { ErrorDetailContainer } from "../../api/config";
import { UserService } from "../../api/psc";
import { ErrorParser, PageHeader } from "../../components/exports";
import { useAppSelector } from "../../hooks/reduxHooks";
import { onUserNationCodeAuthenticated } from "../../slices/SharedSlice";
import { toEnglishDigits } from "../../utils/language";
import { getAuthManager } from "../../utils/auth";

const NationalAuthenticationTypography = styled(Typography)(({ theme }) => ({
    marginBottom: theme.spacing(3),
    fontSize: "1rem",
}));

const Errors = {
    NATIONAL_CODE_NOT_MATCH: ErrorDetailContainer.fromString(
        "شماره موبایل و کد ملی مطابقت ندارند. لطفا کد ملی مربوط به شماره موبایل را وارد کنید"
    ),
    SERVER_ERROR: ErrorDetailContainer.fromString("خطا در ارتباط با سرور"),
};

const validationSchema = z.object({
    nationalCode: z
        .string()
        .regex(/^[0-9۰-۹]{10}$/, { message: "کد ملی الزامی است" }),
    agreeToTermsAndService: z.boolean().refine((value) => value === true, {
        message: "پذیرش قوانین و مقررات الزامی است",
    }),
});

type FormType = z.infer<typeof validationSchema>;

const DEFAULT_VALUES: FormType = {
    nationalCode: "",
    agreeToTermsAndService: false,
};

export default function NationalAuthentication() {
    const dispatch = useDispatch();
    const sharedState = useAppSelector((state) => state.shared);

    const [error, setError] = useState<ErrorDetailContainer>();

    const {
        register,
        handleSubmit,
        formState: { isValid },
    } = useForm<FormType>({
        defaultValues: DEFAULT_VALUES,
        resolver: zodResolver(validationSchema),
    });

    const performNationalAuthentication = useMutation<
        boolean, // response type
        ErrorDetailContainer, // error type
        FormType // request type
    >({
        mutationFn: async (data: FormType) => {
            return await UserService.performNationalAuthentication("1", {
                nationalCode: data.nationalCode,
            });
        },
        onSuccess: (data) => {
            if (!data) {
                setError(Errors.NATIONAL_CODE_NOT_MATCH);
            } else {
                dispatch(onUserNationCodeAuthenticated(data));
            }
        },
        onError: (error) => {
            setError(error);
        },
    });

    const handleSendData = handleSubmit((data) => {
        performNationalAuthentication.reset();
        setError(null);
        performNationalAuthentication.mutate(data);
    });

    const handleLogout = async () => {
        const authenticator = getAuthManager();
        await authenticator.logout();
    };

    return (
        <Container maxWidth={"sm"}>
            <PageHeader title="احراز هویت" />

            <NationalAuthenticationTypography>
                براساس قوانین مصوب، ارائه‌ی هرگونه خدمات آنلاین مستلزم احراز
                هویت کاربران است. از این‌رو بایستی شماره‌ی موبایل و شماره‌ی ملی
                وارد شده به نام شما باشد.
            </NationalAuthenticationTypography>

            <NationalAuthenticationTypography color="primary.main">
                <strong>قوانین مورد اشاره:</strong>
            </NationalAuthenticationTypography>

            <NationalAuthenticationTypography color="secondary.main">
                • قانون مبارزه با پولشویی ماده‌ی ۷ و آیین‌نامه‌ی اجرایی آن
            </NationalAuthenticationTypography>

            <NationalAuthenticationTypography color="secondary.main">
                • مصوبه‌ی شورای عالی فضای مجازی: «نظام هویت معتبر در فضای مجازی
                کشور»
            </NationalAuthenticationTypography>

            <NationalAuthenticationTypography color="secondary.main">
                • مصوبه‌ی شورای اجرایی فناوری اطلاعات: «الزام ارائه خدمات
                الکترونیکی به اشخاص با احراز هویت و نشانی مکان اقامت یگانه»
            </NationalAuthenticationTypography>
            <Card>
                <Stack sx={{ p: 2 }} direction="column" gap={2}>
                    <Typography>
                        برای استفاده از پنل پرشین سازه، باید احراز هویت کنید
                    </Typography>

                    <TextField
                        fullWidth
                        value={
                            sharedState.accountDetail.currentUser.mobileNumber
                        }
                        disabled={true}
                        label={"شماره موبایل"}
                        variant="outlined"
                    />

                    <TextField
                        fullWidth
                        {...register("nationalCode", {
                            setValueAs: (value) => toEnglishDigits(value),
                        })}
                        label={"کد ملی"}
                        variant="outlined"
                    />

                    <Stack direction="column" gap={1}>
                        <Typography>
                            برای استفاده از پنل پرشین سازه، لطفاً ابتدا با
                            قوانین و مقررات موافقت کنید.
                        </Typography>

                        <Link
                            href={"https://persiansaze.com/rules"}
                            target="_blank"
                            sx={{ width: "fit-content" }}
                        >
                            <Typography>لینک صفحه قوانین و مقررات</Typography>
                        </Link>

                        <Stack direction="row" alignItems="center">
                            <FormControlLabel
                                {...register("agreeToTermsAndService")}
                                control={<Checkbox />}
                                label="قوانین و مقررات را میپذیرم"
                            />
                        </Stack>
                    </Stack>

                    <ErrorParser error={error}></ErrorParser>

                    <LoadingButton
                        onClick={handleSendData}
                        loading={performNationalAuthentication.isLoading}
                        variant="contained"
                        disabled={!isValid}
                    >
                        ذخیره
                    </LoadingButton>


                </Stack>
            </Card>

            <Button variant='outlined' sx={{ mt: 2 }} fullWidth color="warning" onClick={handleLogout}>
                خروج از حساب کاربری
            </Button>


        </Container>
    );
}
